/**
 * ConclusionCategoryStatusTable — the golden "Category statuses,
 * evidence-justified" table (golden-convergence GC-W4).
 *
 * The audit's HIGH gap #4 was that NO surface rendered the golden conclusion's
 * 3-column CATEGORY / STATUS (coloured dot) / JUSTIFYING EVIDENCE table. The
 * data already exists — `report.categories` (RagCategory[]) carries `label`,
 * `status` (green/amber/red), and `explanation` (the per-category justifying
 * evidence the score was derived from). This primitive renders exactly that:
 * a clean, evidence-true status table so the conclusion's verdict is backed by
 * a scannable, category-level justification (not just prose).
 *
 * Theme-aware (roleColor — follows the active preset) + null-safe (collapses to
 * null when the report carries no scored categories, e.g. a legacy/unscored
 * report). The status DOT is the primary signal; the band WORD
 * (Strong/Watch/Risk) supports it and reads off the same RagStatus.
 */
import React from 'react';
import type { SiteRagReport, RagStatus } from '../../types/siteRag';
import { useEffectiveReportTheme } from '../../lib/useEffectiveReportTheme';
import { roleColor, type ReportColorRole } from '../../lib/reportRoleColors';
import { buildReportCategoryDisplayRows } from '../../lib/reportCategoryPresentation';
export { sanitizeEvidenceNumbers } from '../../lib/reportCategoryPresentation';

interface Props {
  report: SiteRagReport;
}

const STATUS_ROLE: Record<RagStatus, ReportColorRole> = {
  green: 'positive',
  amber: 'warning',
  red: 'negative',
};

export function ConclusionCategoryStatusTable({ report }: Props): React.ReactElement | null {
  const theme = useEffectiveReportTheme();
  const cats = buildReportCategoryDisplayRows(report);
  if (cats.length === 0) return null;

  const headerStyle: React.CSSProperties = {
    fontFamily: 'var(--report-body-font, inherit)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: roleColor(theme, 'muted'),
    textAlign: 'left',
    padding: '0 12px 7px 0',
    borderBottom: `1.5px solid ${roleColor(theme, 'ink')}`,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      className="mb-6"
      data-testid="conclusion-category-status-table"
      data-paged-split-after="true"
      data-paged-split-kind="conclusion-status"
      // The table is a row stream, not one page-sized exhibit. In the measured
      // deck the shared row-group contract turns this carrier into ordinary
      // block flow while each semantic <tr> remains atomic below.
      data-layout-group="row-group"
      data-paged-table-group="conclusion-status"
    >
      <div
        className="mb-3"
        data-paged-table-label="conclusion-status"
        style={{
          fontFamily: 'var(--report-body-font, inherit)',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: roleColor(theme, 'ink'),
          breakAfter: 'avoid',
          pageBreakAfter: 'avoid',
        }}
      >
        Category statuses, evidence-justified
      </div>
      <table
        data-table-splittable="true"
        data-paged-table-kind="conclusion-status"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          // Opt out of the deck-wide short-table atomic rule. The repeated
          // header + row keeps below provide the safe fragmentation contract.
          breakInside: 'auto',
          pageBreakInside: 'auto',
          breakBefore: 'avoid',
          pageBreakBefore: 'avoid',
        }}
      >
        <colgroup>
          <col style={{ width: '21%' }} />
          <col style={{ width: '23%' }} />
          <col style={{ width: '56%' }} />
        </colgroup>
        <thead data-paged-table-header="conclusion-status">
          <tr>
            <th style={headerStyle}>Category</th>
            <th style={headerStyle}>Status</th>
            <th style={{ ...headerStyle, paddingRight: 0 }}>Justifying evidence</th>
          </tr>
        </thead>
        <tbody data-paged-table-body="conclusion-status">
          {cats.map((c, rowIndex) => {
            const effStatus = c.status;
            const dot = roleColor(theme, STATUS_ROLE[effStatus] ?? 'muted');
            // CSS orphans/widows do not apply to table rows. Keep exactly the
            // first two rows together (row 0 break-after) and exactly the last
            // two together (last row break-before). Do not stamp row 1 or the
            // penultimate row: doing so chains three rows and recreates the
            // large blank tail this table is meant to prevent.
            const rowKeepStyle: React.CSSProperties = {
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
              ...(rowIndex === 0
                ? { breakAfter: 'avoid', pageBreakAfter: 'avoid' }
                : {}),
              ...(rowIndex === cats.length - 1
                ? { breakBefore: 'avoid', pageBreakBefore: 'avoid' }
                : {}),
            };
            return (
              <tr
                key={c.id}
                data-testid={`conclusion-status-row-${c.id}`}
                data-paged-table-row="conclusion-status"
                data-paged-row-key={c.id}
                style={rowKeepStyle}
              >
                <td
                  style={{
                    padding: '9px 12px 9px 0',
                    verticalAlign: 'top',
                    fontWeight: 600,
                    fontSize: 13,
                    color: roleColor(theme, 'ink'),
                    borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
                  }}
                >
                  {c.label}
                </td>
                <td
                  style={{
                    padding: '9px 12px 9px 0',
                    verticalAlign: 'top',
                    borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                    <span
                      aria-hidden="true"
                      style={{ width: 8, height: 8, marginTop: 4, borderRadius: '9999px', background: dot, flex: '0 0 8px' }}
                    />
                    <span style={{ fontSize: 12, lineHeight: 1.35, fontWeight: 600, color: dot }} data-status={effStatus}>
                      {c.band}
                    </span>
                  </span>
                </td>
                <td
                  style={{
                    padding: '9px 0',
                    verticalAlign: 'top',
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: roleColor(theme, 'body'),
                    borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
                  }}
                >
                  {c.evidence}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
